/**
 * The request screen's correspondence state, in one hook (#1069 Story 2.2,
 * D2; FR54, NFR54; the timer, the clocks and revocation since Story 2.3).
 *
 * ⚠️ EXTRACTED FROM `RequestDetail.tsx` SO THAT TESTS RENDER WHAT THE SCREEN
 * RENDERS. Until this story the screen wired `useCorrespondenceSummary` into
 * `useUnreadSpaceState` inline, and two client suites asserted a hand-copied
 * replica of that wiring ("the exact wiring from `RequestDetail.tsx`"). A
 * replica cannot regress with the screen: a `?? previous` fallback keeping a
 * stale dot lit on error would have left both green (deferred-work item 1,
 * Story 1.4's gate review). Now the screen consumes THIS, and the suites render
 * THIS. There is nothing left to copy.
 *
 * What it does, unchanged from the screen:
 *
 *   · `useCorrespondenceSummary(requestId, { panelOpen })`: the FR54 query, a
 *     query of its own beside the other request-detail queries and NOT the
 *     panel's `useCorrespondence`, which runs only while the panel is open.
 *     Degrades to `undefined` (no dot), never to an error surface. Since
 *     Story 2.3 it ticks every 10 s while the panel is open (AC 1, 2).
 *   · `useUnreadSpaceState(...)`: the ONE place the server's answer is
 *     reconciled with what the panel can see. The toggle, the space tabs and
 *     the panel all render the result; the panel's own knowledge flows back
 *     up through `noteLocalAnswer`.
 *   · `handleThreadRead`: the panel has just marked a thread read, so refresh
 *     everything that could still say otherwise.
 *
 * And since Story 2.3:
 *
 *   · `spaceActivity`: the RAW per-space clocks off the summary (never the
 *     merged unread), as two ISO strings, so the panel can re-list the
 *     audience it is showing when its clock moves (FR69). Memoised on the
 *     two strings so an equal-content re-render hands the panel the same
 *     object (C5: primitives, never objects, in dependency lists).
 *   · `accessRevoked` (D3): a 404 from the summary means the server has
 *     stopped answering this person about this request. The summary is
 *     masked to `undefined` (React Query keeps the last `data` beside the
 *     error, which would keep the dot lit), the merged unread is forced
 *     `false` (a local answer the panel reported earlier must not survive as
 *     a lit toggle), and `useCorrespondenceSummary`'s interval stops. The
 *     screen itself is left alone: it refuses on the next navigation
 *     (ITS_TC_288). A 500 or a network failure is NOT revocation (AC 13).
 *   · one `__DEV__` warn on the error TRANSITION (D9, NFR54): ids and the
 *     status only, never a body, never an email. The server already emits
 *     the production signal when its summary service fails
 *     (`correspondence.route.ts`); there is no client logger module. No toast,
 *     no banner: `meta.errorMessage` stays `undefined`.
 *
 * ⚠️ `handleThreadRead` INVALIDATES THREE KEYS, AND THE THIRD IS NEW HERE.
 * `useRequestsPaginatedQuery` sets `staleTime: 30 * 1000` with
 * `refetchOnMount: true` (plain `useRequestsQuery` sets neither; the name was
 * wrong here until the 2026-09-11 code review),
 * and plain `true` RESPECTS `staleTime`, so a list the user returns to inside
 * 30 seconds would still carry whatever it knew before the read; hence
 * `requestsKeys.all` (a prefix of every paginated page, since this screen does
 * not know which page the user came from). `ViewRequests` additionally falls
 * back to `offlineRequestsKeys.lists()` when the paginated page is empty
 * (Story 2.2, C6), and those rows carry the same two booleans, so that list is
 * invalidated too — or the fallback row could contradict the user's own read at
 * exactly the moment they look for confirmation.
 *
 * @see src/components/correspondence/useUnreadSpaceState.ts — the reconciliation
 * @see src/hooks/queries/useCorrespondenceSummary.ts — the query and the timer
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  correspondenceSummaryKeys,
  isAccessRevoked,
  useCorrespondenceSummary,
} from './useCorrespondenceSummary';
import { offlineRequestsKeys } from './useOfflineRequestsQuery';
import { requestsKeys } from './useRequestsQuery';
import { useUnreadSpaceState } from '../../components/correspondence/useUnreadSpaceState';

import type { CorrespondenceSummary } from './useCorrespondenceSummary';
import type { SpaceActivity } from '../../components/correspondence/correspondence.types';
import type { UnreadSpaceState } from '../../components/correspondence/useUnreadSpaceState';

export type { SpaceActivity };

export interface RequestCorrespondenceStateOptions {
  /** The discussion panel is open: run the 10 s timer (Story 2.3, AC 1, 2). */
  panelOpen?: boolean;
}

export interface RequestCorrespondenceState extends UnreadSpaceState {
  /**
   * The server's FR54 answer as fetched; `undefined` while loading, failed,
   * or after access to the request has been revoked (Story 2.3, D3).
   */
  summary: CorrespondenceSummary | undefined;
  /** The raw per-space clocks off `summary`, never the merged unread. */
  spaceActivity: SpaceActivity;
  /** The summary answered 404: this person no longer has this request. */
  accessRevoked: boolean;
  /** The panel has marked a thread read: invalidate what could still disagree. */
  handleThreadRead: () => void;
}

export const useRequestCorrespondenceState = (
  requestId?: string,
  { panelOpen = false }: RequestCorrespondenceStateOptions = {},
): RequestCorrespondenceState => {
  const queryClient = useQueryClient();

  const { data, error } = useCorrespondenceSummary(requestId || undefined, {
    panelOpen,
  });

  const accessRevoked = isAccessRevoked(error);
  // React Query keeps the last `data` beside `error`; after a revoke that
  // last answer must not keep a dot lit anywhere (D3).
  const summary = accessRevoked ? undefined : data;

  /**
   * D9: one warn per error TRANSITION, not per tick while the error persists.
   * `__DEV__` only; the server's warn is the production signal. Ids and the
   * status, never the error's message (a body, a hostname, an address).
   */
  const warnedRef = useRef(false);
  useEffect(() => {
    if (!error) {
      warnedRef.current = false;
      return;
    }
    if (warnedRef.current) return;
    warnedRef.current = true;
    if (__DEV__) {
      const status = (error as { status?: unknown }).status;
      console.warn({
        operation: 'correspondence.summary.refresh',
        requestId,
        status: typeof status === 'number' ? status : undefined,
      });
    }
  }, [error, requestId]);

  const { spaceUnread, hasUnread, noteLocalAnswer } = useUnreadSpaceState({
    hasUnread: summary?.hasUnread,
    internal: summary?.spaces?.internal?.hasUnread,
    shared: summary?.spaces?.shared?.hasUnread,
    hasSpaces: Boolean(summary?.spaces),
  });

  // C5: two strings in, one object out, and the same object while the
  // strings hold. The panel keys an effect on the strings, not on this.
  const internalActivity = summary?.spaces?.internal?.lastActivityAt;
  const sharedActivity = summary?.spaces?.shared?.lastActivityAt;
  const spaceActivity = useMemo<SpaceActivity>(
    () => ({ internal: internalActivity, shared: sharedActivity }),
    [internalActivity, sharedActivity],
  );

  const handleThreadRead = useCallback(() => {
    if (requestId) {
      queryClient.invalidateQueries({
        queryKey: correspondenceSummaryKeys.detail(requestId),
      });
    }
    queryClient.invalidateQueries({ queryKey: requestsKeys.all });
    queryClient.invalidateQueries({ queryKey: offlineRequestsKeys.lists() });
  }, [queryClient, requestId]);

  // A held local answer (the panel once said "unread here") must not survive
  // a revoke as a lit toggle; nothing may be shown for a request the person
  // no longer has (D3, AC 10).
  const REVOKED_UNREAD = { internal: false, shared: false } as const;

  return {
    summary,
    spaceUnread: accessRevoked ? REVOKED_UNREAD : spaceUnread,
    hasUnread: accessRevoked ? false : hasUnread,
    noteLocalAnswer,
    handleThreadRead,
    spaceActivity,
    accessRevoked,
  };
};
